import useSegmentTracking from 'core/utils/segment/useSegmentTracking';
import type { Feature, GeoJsonProperties, Point } from 'geojson';
import type { GeoJSONSource } from 'mapbox-gl';
import mapboxgl from 'mapbox-gl';
import ClusterPopupCard from '@features/regional-overview/components/cluster-popup-card/cluster-popup-card.component';
import { ClusterFieldsContent } from '@features/regional-overview/components/cluster-popup-card/cluster-fields-content.component';
import { ClusterPopupStyles } from '@features/regional-overview/components/cluster-popup-card/cluster-popup-card.styles';
import type { RegionalOverviewV2Properties } from 'querys/regional-overview/regional-overview.query.models';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { COLLAPSED_SHEET_HEIGHT_PX, FIELD_ZOOM_LEVEL, MAX_CLUSTER_LEAVES } from './use-cluster-popup.constants';
import { CLUSTER_SOURCE_ID } from './use-overview-points-cluster.constants';
import type { FieldPopupData } from './use-overview-points-popup.hook';

interface UseClusterPopupProps<T extends GeoJsonProperties> {
  map?: mapboxgl.Map;
  onFieldSelect?: (data: FieldPopupData) => void;
  mapPointFeatures?: Feature<Point, T>[];
  isMobile?: boolean;
}

export const useClusterPopup = <T extends GeoJsonProperties>({
  map,
  onFieldSelect,
  mapPointFeatures,
  isMobile = false
}: UseClusterPopupProps<T>) => {
  const [popupState, setPopupState] = useState<{
    fields: (RegionalOverviewV2Properties & { coordinates: [number, number] })[];
    lngLat: mapboxgl.LngLatLike;
  } | null>(null);
  const popupContainerRef = useRef<HTMLDivElement | null>(null);
  const mapPopupRef = useRef<mapboxgl.Popup | null>(null);
  const segmentTracking = useSegmentTracking();

  useEffect(() => {
    popupContainerRef.current = document.createElement('div');

    return () => {
      popupContainerRef.current = null;
    };
  }, []);

  const handleClose = useCallback(() => {
    setPopupState(null);
    mapPopupRef.current?.remove();
    mapPopupRef.current = null;
  }, []);

  const handleFieldClick = useCallback(
    (fieldId: string) => {
      if (!map || !popupState) return;

      const field = popupState.fields.find(field => field.id === fieldId);
      if (!field) return;

      segmentTracking.track('Regional Risk - Cluster Field Selected', { fieldId });

      handleClose();

      map.flyTo({
        center: field.coordinates,
        zoom: FIELD_ZOOM_LEVEL,
        essential: true,
        ...(isMobile && { padding: { top: 0, bottom: COLLAPSED_SHEET_HEIGHT_PX, left: 0, right: 0 } })
      });

      map.once('moveend', () => {
        onFieldSelect?.({ properties: field, coordinates: field.coordinates });
      });
    },
    [map, popupState, handleClose, onFieldSelect, isMobile, segmentTracking]
  );

  const handleClusterClick = useCallback(
    (clusterId: number, lngLat: mapboxgl.LngLatLike) => {
      if (!map || !popupContainerRef.current) return;

      const source = map.getSource(CLUSTER_SOURCE_ID) as GeoJSONSource | undefined;

      if (!source) return;

      source.getClusterLeaves(clusterId, MAX_CLUSTER_LEAVES, 0, (error, leaves) => {
        if (error || !leaves?.length) return;

        const fields = (leaves as Feature<Point, RegionalOverviewV2Properties>[]).map(feature => ({
          ...feature.properties,
          coordinates: feature.geometry.coordinates as [number, number]
        }));

        segmentTracking.track('Regional Risk - Cluster Opened', { fieldCount: fields.length });
        setPopupState({ fields, lngLat });
      });
    },
    [map, segmentTracking]
  );

  useEffect(() => {
    handleClose();
  }, [mapPointFeatures, handleClose]);

  useEffect(() => {
    if (!map || !popupContainerRef.current || !popupState || isMobile) return;

    mapPopupRef.current?.remove();

    mapPopupRef.current = new mapboxgl.Popup({
      closeButton: false,
      anchor: 'top',
      offset: [0, 20],
      className: 'cluster-popup'
    });

    mapPopupRef.current.setLngLat(popupState.lngLat).setDOMContent(popupContainerRef.current).addTo(map);

    return () => {
      mapPopupRef.current?.remove();
    };
  }, [map, popupState, isMobile]);

  const portalContent = useMemo(() => {
    if (isMobile || !popupState || !popupContainerRef.current) return null;

    return createPortal(
      <>
        <ClusterPopupStyles />
        <ClusterPopupCard clusterLeaves={popupState.fields} onClose={handleClose} onFieldClick={handleFieldClick} />
      </>,
      popupContainerRef.current
    );
  }, [isMobile, popupState, handleClose, handleFieldClick]);

  const content = useMemo<ReactNode>(() => {
    if (!isMobile || !popupState) return null;

    return <ClusterFieldsContent clusterLeaves={popupState.fields} onClose={handleClose} onFieldClick={handleFieldClick} inSheet />;
  }, [isMobile, popupState, handleClose, handleFieldClick]);

  return { portalContent, isOpen: !!popupState, content, onClose: handleClose, onClusterClick: handleClusterClick };
};
